/**
 * 下半分のミニ盤（Issue #467）。上半分の盤は押さないので、場（モンスター / 魔法罠 / フィールド）を下半分に小さく描いて押させる。
 * 並びは上半分の動画と同じ: 上に P2（魔法罠の行 → モンスターの行）、下に P1（モンスターの行 → 魔法罠の行）。番号は左から 1〜5、
 * フィールドゾーンは動画と同じく P1 は右端・P2 は左端。
 *
 * マスの鍵は盤面のカードの鍵と同じ形（`p:monster_field:i` / `p:magic_trap:i`。gameUi.placeKey / promptCardKey と同じ）なので、
 * 置く場所の選択（MiniField）とカードの選択（RespondTab が MiniBoard を直接使う）で同じ部品になる。
 */
import { useState } from 'react';
import { playerColor, usePlayerColors } from './common';
import { pickPlace, placeKey } from './inputView';

export const CELL_W = 50;
export const CELL_H = 56;

/** 行の並び: [{ player, zone, label, cells: [sequence | null（空き地）] }] */
export function miniRows() {
  const five = [0, 1, 2, 3, 4];
  return [
    { player: 1, zone: 'magic_trap', label: 'P2 魔法罠', cells: [5, ...five] },
    { player: 1, zone: 'monster_field', label: 'P2 モンスター', cells: [null, ...five] },
    { player: 0, zone: 'monster_field', label: 'P1 モンスター', cells: [...five, null] },
    { player: 0, zone: 'magic_trap', label: 'P1 魔法罠', cells: [...five, 5] },
  ];
}

/**
 * enabled（押せるマスの鍵）/ selected（選んでいる鍵）/ badges（鍵 → 札の文字）/ onTap(key)。
 * board があれば、そのマスにあるカードを描く（表側は画像、裏側は暗い板、守備表示は横）
 */
export function MiniBoard({ board = null, thumbs = {}, enabled = new Set(), selected = new Set(), badges = new Map(), busy = false, onTap }) {
  const colors = usePlayerColors();
  return (
    <div data-testid="mini-board" style={{ display: 'flex', flexDirection: 'column', gap: 3, alignItems: 'center' }}>
      {miniRows().map((row, ri) => (
        <div key={ri} style={{ display: 'flex', gap: 3, alignItems: 'center', marginTop: ri === 2 ? 5 : 0 }}>
          {row.cells.map((seq, ci) => {
            if (seq == null) return <span key={ci} style={{ width: CELL_W, height: CELL_H }} />;
            const key = `${row.player}:${row.zone}:${seq}`;
            const ok = enabled.has(key);
            const on = selected.has(key);
            const card = board?.[row.zone]?.[row.player]?.[seq] ?? null;
            const faceUp = card && card.is_face_up !== false;
            const image = faceUp ? thumbs[String(card.card_id)] : null;
            const defense = card && card.battle_pos !== 'attack' && row.zone === 'monster_field';
            const color = playerColor(row.player, colors);
            const label = seq === 5 ? 'F' : String(seq + 1);
            const badge = badges.get(key);
            return (
              <button key={ci} type="button" data-testid="mini-cell" data-key={key} data-place={key} disabled={!ok || busy} aria-pressed={on}
                aria-label={`${row.label} ${seq === 5 ? 'フィールド' : seq + 1}`} onClick={() => onTap?.(key)}
                style={{ position: 'relative', width: CELL_W, height: CELL_H, padding: 0, borderRadius: 6, overflow: 'hidden', cursor: ok ? 'pointer' : 'default',
                  border: `2px solid ${on ? '#ffd36b' : ok ? '#4fc3ff' : color}`, opacity: ok || card ? 1 : 0.35,
                  background: on ? '#3b3420' : ok ? '#1b3a55' : 'transparent', color: '#e8eefc', filter: !ok && enabled.size ? 'brightness(0.55)' : undefined }}>
                {card ? (
                  image ? <img alt="" src={image} style={{ position: 'absolute', left: '50%', top: '50%', width: CELL_H * 0.62, height: CELL_H * 0.9, objectFit: 'cover',
                    transform: `translate(-50%, -50%) rotate(${(row.player === 1 ? 180 : 0) + (defense ? 90 : 0)}deg)`, borderRadius: 2 }} />
                    : <span style={{ position: 'absolute', left: '50%', top: '50%', width: CELL_H * 0.62, height: CELL_H * 0.9, background: '#141a2e', border: `1px solid ${color}`,
                      transform: `translate(-50%, -50%) rotate(${defense ? 90 : 0}deg)`, borderRadius: 2, boxSizing: 'border-box' }} />
                ) : null}
                <span style={{ position: 'absolute', left: 2, top: 0, fontSize: 11, fontWeight: 800, textShadow: '0 0 3px #000' }}>{label}</span>
                {badge != null ? <span style={{ position: 'absolute', right: 1, bottom: 1, minWidth: 16, padding: '0 3px', borderRadius: 999, background: '#ffd36b', color: '#1b1308', fontSize: 11, fontWeight: 800 }}>{badge}</span> : null}
              </button>
            );
          })}
        </div>
      ))}
    </div>
  );
}

/** 置く場所を選ぶ。count か所そろったら onDone(places)。置けないマスは押せない */
export default function MiniField({ options = [], count = 1, board = null, thumbs = {}, busy = false, onDone, onPicking }) {
  const [picked, setPicked] = useState([]);
  const allowed = new Map(options.map((p) => [placeKey(p), p]));
  const tap = (key) => {
    const place = allowed.get(key);
    if (!place) return;
    const r = pickPlace(picked, place, count);
    if (r.done) { setPicked([]); onDone?.(r.places); return; }
    setPicked(r.places);
    onPicking?.(r.places);
  };
  return (
    <div data-testid="mini-field" style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'center' }}>
      {count > 1 ? <div style={{ fontSize: 12, opacity: 0.8 }}>{count} か所選ぶ（{picked.length} / {count}）</div> : null}
      <MiniBoard board={board} thumbs={thumbs} enabled={new Set(allowed.keys())} selected={new Set(picked.map(placeKey))} busy={busy} onTap={tap} />
    </div>
  );
}
